<template>
  <div class="view-container">
    <div class="card">
      <div class="card-header card-header-divider">
        <div class="card-title">
          Список текстов для отчётов
        </div>
        <div>
          <button @click="() => router.push('/new_reports_text/edit')" class="btn btn-accent">Добавить текст</button>
        </div>
      </div>
      <div class="card-body">
        <div class="flex justify-content-start reports-text-filters align-item-center">
          <div class="title_search_wrapper">
            <custom-input
                :delay="800"
                name="reports-text-title"
                label="Название"
                :label-width="30"
                :inputWidth="70"
                cleanable
                v-model="searchParams.title"
            />
          </div>
          <div class="report_type_select">
            <custom-select-2
                v-model="searchParams.report_type"
                name="role"
                label="Роль"
                :options="[
                 {id: 'employee', text: 'Сотрудник'},
                 {id: 'employer', text: 'Заказчик'},
                 {id: 'standard', text: 'Стандартный'}
               ]"
            />
          </div>
          <div class="button_wrapper">
            <button @click="search" class="btn btn-accent clear-btn">Искать</button>
            <button @click="clearSearch" class="btn btn-w clear-btn">Очистить</button>
          </div>
        </div>
        <div class="score_button_wrapper">
          <button @click="() => showScoreFilter = !showScoreFilter" class="btn btn-w">{{ showScoreFilter ? 'Скрыть фильтр' : 'Показать фильтр'}} по баллам</button>
        </div>
        <div v-if="showScoreFilter" class="scores_wrapper">
          <div class="score_group">
            <p>Балл 1-го раздела</p>
            <div class="input_group">
              <custom-input name="score1_from"
                            type="number"
                            label="От"
                            v-model="searchParams.score1_from"
              />
              <custom-input name="score1_to"
                            type="number"
                            label="До"
                            v-model="searchParams.score1_to"
              />
            </div>
          </div>
          <div class="score_group">
            <p>Балл 2-го раздела</p>
            <div class="input_group">
              <custom-input name="score2_from"
                            type="number"
                            label="От"
                            v-model="searchParams.score2_from"
              />
              <custom-input name="score2_to"
                            type="number"
                            label="До"
                            v-model="searchParams.score2_to"
              />
            </div>
          </div>
        </div>
        <div class="text_input">
          <custom-input
              :delay="800"
              name="reports-text"
              label="Текст"
              :label-width="14"
              :inputWidth="100"
              cleanable
              v-model="searchParams.text"
          />
        </div>

        <div v-if="!loadPreloader && rowData.length">
          <base-table
              :columns=reportTextTable
              :rows=rowData
              :sortField="searchParams.sort_field"
              :sortDirection="searchParams.sort"
              @sort="(data) => {
                searchParams.sort_field = data.sortValue;
                searchParams.sort = data.direction;
                search();
                }"
          />

          <table-pagination
              :countOnPage="countOnPage"
              :total="totalListCount"
              :count="rowData?.length"
              :startPage="currentPage"
              @changePage="changePage"
          />
        </div>
        <div v-if="loadPreloader" class="table-preloader">
          <preloader/>
        </div>
        <div v-if="!loadPreloader && !rowData.length">
          <p class="no_results">
            Нет результатов
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import useCheckRights from "@/mixins/useCheckRights";
  import {computed, reactive, ref} from "@vue/reactivity";
  import {useStore} from "vuex";
  import {useRouter} from "vue-router";
  import {onBeforeMount, watch} from "@vue/runtime-core";
  import TablePagination from "@/components/OtherElements/TablePagination.vue";
  import CustomSelect2 from "@/components/Forms/Fields/CustomSelect2.vue";
  import CustomInput from "@/components/Forms/Fields/CustomInput.vue";
  import Preloader from "@/components/Technical/Preloader.vue";
  import BaseTable from "@/components/Tables/BaseTable.vue";

  const {getRight} = useCheckRights();
  const user = computed(() => store.state.profile.profile);
  const store = useStore();
  const router = useRouter();

  const loadPreloader = ref(false);
  const currentPage = ref(1);
  const showScoreFilter = ref(false);

  const reportTypeTranslation = {
    employee: 'Сотрудник',
    employer: 'Заказчик',
    standard: 'Стандартный',
  }

  const countOnPage = ref(25),
      searchParams = reactive({
        title: "",
        text: "",
        report_type: "employee",
        score1_from: 0,
        score1_to: 150,
        score2_from: 0,
        score2_to: 150,
        sort_field: '',
        sort: '',
        per_page: countOnPage.value,
        page: currentPage.value,
      });

  const reportTexts = computed(() => store.state.newReport.textList),
      totalListCount = computed(() => store.state.newReport.totalListCount),
      rowData = computed(() => {
        return reportTexts.value.map((text) => {
          return {
            id: text.id,
            title: text.title,
            text: text.text,
            report_type: reportTypeTranslation[text.report_type],
            scores1_from: text.scores1_from,
            scores1_to: text.scores1_to,
            scores2_from: text.scores2_from,
            scores2_to: text.scores2_to,
          };
        })
      });

  const clearSearch = () => {
    searchParams.text = "";
    searchParams.title = "";
    searchParams.score1_from = 0;
    searchParams.score1_to = 150;
    searchParams.score2_from = 0;
    searchParams.score2_to = 150;
    searchParams.page = 1;
    searchParams.sort = '';
    searchParams.sort_field = '';
  };

  const reportTextTable = [
    {
      field: "title",
      headerName: "Название",
      link: true,
      dest: 'new_reports_text',
      res: "edit",
      sortable: true,
    },
    {
      field: "text",
      headerName: "Текст",
      longText: true,
    },
    {
      field: "scores1_from",
      headerName: "Балл 1-го раздела от",
      sortable: true,
    },
    {
      field: "scores1_to",
      headerName: "Балл 1-го раздела до",
      sortable: true,
    },
    {
      field: "scores2_from",
      headerName: "Балл 2-го раздела от",
      sortable: true,
    },
    {
      field: "scores2_to",
      headerName: "Балл 2-го раздела до",
      sortable: true,
    },
    {
      field: "report_type",
      headerName: "Тип отчёта",
    },
    {
      field: "edit",
      headerName: "",
      dest: "new_reports_text",
      res: "edit"
    },
  ];

  const search = () => {
    loadPreloader.value = true;
    currentPage.value = 1;
    searchRequest();
  }

  const searchRequest = () => {
    loadPreloader.value = true;

    searchParams.first_score = [parseInt(searchParams.score1_from), parseInt(searchParams.score1_to)];
    searchParams.second_score = [parseInt(searchParams.score2_from), parseInt(searchParams.score2_to)];
    searchParams.per_page = countOnPage.value;
    searchParams.page = currentPage.value;

    if (searchParams.sort_field) {
        searchParams.sort_direction = searchParams.sort === 'up' ? 'asc' : 'desc';
    }

    store
        .dispatch("newReport/getTexts", searchParams)
        .finally(() => (loadPreloader.value = false));
  };

  const changePage = (page) => {
    if (page !== currentPage.value) {
      currentPage.value = page;
      loadPreloader.value = true;
      searchRequest();
    }
  };

  onBeforeMount(() => {
    if (getRight('reportTextEdition') && user.value?.id) {
      loadPreloader.value = true;
      store.dispatch('newReport/getTexts', searchParams).then(() => {
        loadPreloader.value = false;
      });
    }
  });

  watch(() => user.value,() => {
    if (user.value?.id) {
      if (!getRight('reportTextEdition')) {
        router.push('/');
      } else if (!reportTexts.value.length) {
        loadPreloader.value = true;
        store.dispatch('newReport/getTexts', searchParams).then(() => {
          loadPreloader.value = false;
        });
      }
    }
  });
</script>

<style scoped lang="scss">
  .scores_wrapper {
    margin-top: 20px;
    display: flex;
  }

  .score_group {
    padding: 0 10px;
  }

  .button_wrapper {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    row-gap: 5px;
  }

  .reports-text-filters {
    width: 100%;
  }

  .reports-text-filters.form-group {
    width: 300px;
  }

  .report_type_select {
    width: 300px;
  }

  .text_input {
    margin-top: 20px;
    width: 100%;
  }

  .score_button_wrapper {
    margin: 5px 0;
    padding: 0 10px;
  }

  .title_search_wrapper {
    width: 400px;
  }

  @media (max-width: 1110px) {
    .reports-text-filters {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }

    .title_search_wrapper {
      width: 100%;
    }
    .button_wrapper {
      flex-direction: initial;
      margin: 10px 0 10px auto;
      column-gap: 10px;
      order: 1;
    }

  }

  @media (max-width: 940px) {
    .reports-text-filters .form-group {
      order: 2;
    }

    .score_button_wrapper {
      padding: 0;
    }
  }

  @media (max-width: 768px) {
    .scores_wrapper {
      margin-top: 10px;
      flex-direction: column;
    }

    .score_group {
      padding: 5px 0;
    }

    .input_group {
      display: flex;
      column-gap: 10px;
    }
  }
</style>